import { CompetenceModel } from './../../../../../../core/src/lib/models/competence.model';
import { ActivatedRoute } from '@angular/router';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { AcModel } from '../../../models/ac.model';
import { RunnerOnlinetestModel } from '../../../models/runner-onlinetest.model';
import { DataService } from '../../../services/data.service';
import { ApiService } from '../../../services/api.service';
import { AuthStateService } from '../../../services/auth-state.service';
import { RatingtypeModel } from '../../../models/ratingtype.model';
import { TranslocoService } from '@ngneat/transloco';
import { UiService } from '../../../services/ui.service';
import { Subscription, catchError, of, switchMap, takeWhile, timer } from 'rxjs';
import { SliderTypes } from '../../../partials/ui/rating-slider/rating-slider.component';
import { CandidateModel } from '../../../models/candidate.model';
import { AppService } from '../../../services/app.service';
import { ObserverModel } from '../../../models/observer.model';
import { faBan } from '@fortawesome/pro-light-svg-icons';

const POLLING_INTERVAL = 5;
const START_VALUE = -1;

@Component({
  selector: 'app-onlinetest',
  templateUrl: './onlinetest.component.html',
  styleUrls: ['./onlinetest.component.scss']
})
export class OnlinetestComponent implements OnInit, OnDestroy {

  public ac: AcModel = null;
  public onlinetest: RunnerOnlinetestModel = null;
  public candidate: CandidateModel = null;
  public competences: Array<CompetenceModel> = [];
  public ratingtype: RatingtypeModel = null;
  public ratings: any = {};
  public firstRun = true;
  public language: string = null;
  public foreignRatings: any = null;
  private ratingSubscription: Subscription = null;
  private acSubscription: Subscription = null;
  private polling: Subscription = null;
  public withObservers = false;

  public SliderTypes = SliderTypes;
  public faBan = faBan;

  constructor(
    private route: ActivatedRoute,
    private data: DataService,
    private app: AppService,
    private api: ApiService,
    private ui: UiService,
    public auth: AuthStateService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.ui.setPageTitle(this.transloco.translate('common.labels.onlinetest'));
    this.language = this.transloco.getActiveLang();
    this.route.params.subscribe((params) => {
      this.acSubscription = this.data.ac$.subscribe({
        next: (ac: AcModel) => {
          if (ac !== null) {
            this.ac = ac;
            this.candidate = this.ac.candidates[0] || null;
            this.data.setActiveNav('onlinetest');
            const onlinetest = this.ac.getOnlinetestOnId(parseInt(params.onlinetest, 10));
            if (onlinetest !== null) {
              this.onlinetest = onlinetest;
              this.ratingtype = this.ac.ratingtype;
              this.initCandidateValues();
              this.withObservers = this.onlinetest.with_observers;
              if (this.withObservers === true) {
                this.initPolling();
              }
            } else {
              this.app.redirectToDashboard();
            }
          }
        }
      });
    });
    this.transloco.langChanges$.subscribe(() => {
      this.language = this.transloco.getActiveLang();
    });
    // subscribe to ratings
    this.ratingSubscription = this.api.rating$.subscribe({
      next: (result: any) => {
        const ratings = [];
        if (result.data && result.data.length > 0) {
          result.data.forEach((rating: any) => {
            ratings.push(rating);
          });
          this.ac.ratings = ratings;
        }
      },
      error: () => {
        this.ui.toastError(this.transloco.translate('onlinetest.messages.rating_not_saved'));
      }
    });
  }

  ngOnDestroy(): void {
    if (this.polling !== null) {
      this.polling.unsubscribe();
      this.polling = null;
    }
    if (this.acSubscription !== null) {
      this.acSubscription.unsubscribe();
      this.acSubscription = null;
    }
    if (this.ratingSubscription !== null) {
      this.ratingSubscription.unsubscribe();
    }
  }

  public changeCandidate(candidate: CandidateModel): void {
    this.candidate = candidate;
    this.initCandidateValues();
  }

  public initCandidateValues(): void {
    this.ratings = [];
    if (this.onlinetest !== null && this.candidate !== null) {
      this.ac.ratings.filter((rating: any) => {
        return rating.onlinetest === this.onlinetest.onlinetest_id && rating.candidate === this.candidate.hashed_id;
      }).forEach((rating: any) => {
        this.ratings[rating.competence] = {
          value: rating.value,
          behavioranchors: rating.behavioranchors
        };
      });
      this.resetValues();
    }
    if (this.firstRun === true) {
      this.initCompetences();
      this.firstRun = false;
    }
  }

  public calcCompetence(competence: CompetenceModel): void {
    let sum = 0;
    let count = 0;
    competence.pivot?.behavioranchors?.forEach((behavioranchor: any) => {
      if (behavioranchor.value >= 0) {
        sum += behavioranchor.value;
        count++;
      }
    });
    competence.value = (count > 0) ? Math.round(sum / count * (1 / this.ratingtype.step)) * this.ratingtype.step : -1;
  }

  public saveRating(): void {
    this.api.setRating('onlinetest', this.onlinetest?.id, this.candidate?.hashed_id, this.getPayload(), null);
  }

  public getForeignValue(hash: string, competence: CompetenceModel): string {
    if (
      this.candidate &&
      this.foreignRatings[this.candidate.hashed_id] &&
      this.foreignRatings[this.candidate.hashed_id][hash] &&
      this.foreignRatings[this.candidate.hashed_id][hash][competence.id]
    ) {
      const value = this.foreignRatings[this.candidate.hashed_id][hash][competence.id].value;
      if (value >= 0) {
        return (this.auth.isModerator()) ? value : '<i class="pi pi-check">';
      } else {
        return '-';
      }
    }
    return '';
  }

  public isActive(): boolean {
    return !this.ac.isFinished() && (this.auth.isModerator() || this.auth.isObserver()) && !this.auth.isSilentObserver();
  }

  private getPayload(): any {
    const payload = {};
    this.competences.forEach((competence: CompetenceModel) => {
      const behavioranchors = {};
      competence.pivot?.behavioranchors?.forEach((behavioranchor: any) => {
        behavioranchors[behavioranchor.id] = behavioranchor.value;
      });
      payload[competence.id] = {
        value: competence.value,
        behavioranchors
      };
    });
    return payload;
  }

  private initCompetences(): void {
    this.competences = this.onlinetest.competences;
    this.resetValues();
    this.sortCompetences();
  }

  private resetValues(): void {
    const START_VALUE = -1;
    this.competences.forEach((competence: CompetenceModel) => {
      const rating = this.ratings[competence.id] || null;
      if (rating !== null) {
        competence.value = this.ratings[competence.id].value;
        competence.pivot?.behavioranchors?.forEach((behavioranchor: any) => {
          if (rating.behavioranchors[behavioranchor.id]) {
            behavioranchor.value = rating.behavioranchors[behavioranchor.id];
          } else {
            behavioranchor.value = START_VALUE;
          }
        });
      } else {
        competence.value = START_VALUE;
        competence.pivot?.behavioranchors?.forEach((behavioranchor: any) => {
          behavioranchor.value = START_VALUE;
        });
      }
    });
  }

  private sortCompetences(): void {
    this.competences.sort((a: any, b: any) => (
      a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0)
    );
  }

  private initForeignRatings(): void {
    if (Array.isArray(this.ac?.candidates) && Array.isArray(this.ac?.observers)) {
      this.foreignRatings = {};
      this.ac.candidates.forEach((candidate: CandidateModel) => {
        this.foreignRatings[candidate.hashed_id] = {};
        // observers
        this.ac.observers.forEach((observer: ObserverModel) => {
          const competenceData = {};
          this.competences.forEach((competence: CompetenceModel) => {
            competenceData[competence.id] = {
              value: START_VALUE,
              behavioranchors: null
            };
          });
          this.foreignRatings[candidate.hashed_id][observer.hashed_id] = competenceData;
        });
        // moderator
        if (this.auth.isModerator()) {
          const competenceData = {};
          this.competences.forEach((competence: CompetenceModel) => {
            competenceData[competence.id] = {
              value: START_VALUE,
              behavioranchors: null
            };
          });
          this.foreignRatings[candidate.hashed_id][this.ac.moderator?.hashed_id] = competenceData;
        }
      });
    }
  }

  private initPolling(): void {
    if (this.polling === null) {
      this.initForeignRatings();
      this.polling = timer(0, POLLING_INTERVAL * 1000)
        .pipe(
          takeWhile(() => this.auth.isModerator() === true),
          switchMap(() => {
            return this.api.getOnlinetestRatings(this.onlinetest.id)
              .pipe(
                catchError(() => {
                  return of(null);
                })
              );
          })
        ).subscribe((result: any) => {
          if (result !== null) {
            result.data?.forEach((rating: any) => {
              if (
                this.foreignRatings[rating.candidate] !== undefined &&
                this.foreignRatings[rating.candidate][rating.rater] !== undefined &&
                this.foreignRatings[rating.candidate][rating.rater][rating.competence] !== undefined
              ) {
                this.foreignRatings[rating.candidate][rating.rater][rating.competence] = {
                  value: rating.value,
                  behavioranchors: rating.behavioranchors
                };
              }
            });
          }
        });
    }
  }
}
